/**
 * Servicio de autenticación para comunicarse con la API de autenticación
 * Este servicio implementa los métodos necesarios para interactuar con el módulo de autenticación
 * según la documentación proporcionada.
 */
import apiClient from '@/plugins/axios';

// Función auxiliar para realizar peticiones a la API de autenticación
const authRequest = (method, url, data = null) => {
  const config = {
    url,
    method,
    baseURL: `${apiClient.defaults.baseURL}/auth`
  };
  
  if (data) {
    config.data = data;
  }
  
  return apiClient(config);
};


/**
 * Servicio de autenticación
 */
const authService = {
  /**
   * Inicia sesión con usuario y contraseña
   * @param {string} usuario - Nombre de usuario
   * @param {string} password - Contraseña del usuario
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  login(usuario, password) {
    return authRequest('post', '/login', { usuario, password })
      .then(response => {
        // Guardar el token en localStorage para futuras peticiones
        if (response.data && response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('userData', JSON.stringify(response.data.user));
        }
        return response.data;
      });
  },

  /**
   * Cierra la sesión del usuario actual
   * @returns {void}
   */
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
  },

  /**
   * Verifica si el usuario está autenticado
   * @returns {boolean} - True si el usuario está autenticado, false en caso contrario
   */
  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  /**
   * Obtiene los datos del usuario actual
   * @returns {Object|null} - Datos del usuario o null si no está autenticado
   */
  getUserData() {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  },

  /**
   * Obtiene todos los usuarios (requiere autenticación)
   * @returns {Promise} - Promesa con la lista de usuarios
   */
  getUsers() {
    return authRequest('get', '/users');
  },

  /**
   * Obtiene un usuario por su ID (requiere autenticación)
   * @param {number} id - ID del usuario
   * @returns {Promise} - Promesa con los datos del usuario
   */
  getUserById(id) {
    return authRequest('get', `/users/${id}`);
  },

  /**
   * Obtiene todos los sistemas disponibles (requiere autenticación)
   * @returns {Promise} - Promesa con la lista de sistemas
   */
  getSystems() {
    return authRequest('get', '/systems');
  },

  /**
   * Obtiene todos los roles disponibles (requiere autenticación)
   * @returns {Promise} - Promesa con la lista de roles
   */
  getRoles() {
    return authRequest('get', '/roles');
  },

  /**
   * Obtiene los roles específicos del sistema de verificadora (requiere autenticación)
   * @returns {Promise} - Promesa con la lista de roles de verificadora
   */
  getVerificadoraRoles() {
    return authRequest('get', '/verificadora/roles');
  },

  /**
   * Asigna un sistema a un usuario (requiere autenticación)
   * @param {number} userId - ID del usuario
   * @param {number} systemId - ID del sistema
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  assignSystemToUser(userId, systemId) {
    return authRequest('post', `/users/${userId}/systems`, { id_sistema: systemId });
  },

  /**
   * Asigna un rol a un usuario (requiere autenticación)
   * @param {number} userId - ID del usuario
   * @param {number} roleId - ID del rol
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  assignRoleToUser(userId, roleId) {
    return authRequest('post', `/users/${userId}/roles`, { id_rol: roleId });
  }
};

export default authService;
